/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, ReactElement } from 'react';
import {
  BoxProps,
  Text,
  Table,
  Flex,
  VStack,
  Pagination,
  TableProps,
} from '@wegroup/design-system';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  ColumnDef,
  SortingState,
  flexRender,
} from '@tanstack/react-table';
import ReactTableContainer from '../ReactTable/ReactTableContainer';
import ReactTableV8Head from './ReactTableV8Head';
import ReactTableV8Body from './ReactTableV8Body';
import ReactTableV8Footer from './ReactTableV8Footer';
import { useBoolean } from '@chakra-ui/hooks';
import TableBodySkeletonLoading from '../ReactTable/TableBodySkeletonLoading';
import TableFiller from '../ReactTable/TableFiller';

export interface CustomColumnProps {
  isSticky?: boolean;
  isRowSpanned?: boolean;
  dependencies?: string[];
}

export interface TableData {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [col: string]: any;
}

export interface ReactTableV8Props<TData extends TableData> {
  data: TData[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<TData, any>[];
  minWidth?: string | number;
  isHeaderSticky?: boolean;
  pagination?: boolean;
  initialState?: {
    sorting?: SortingState;
    pagination?: {
      pageIndex: number;
      pageSize: number;
    };
  };
  isLoading?: boolean;
  onSort?: (sortArray: SortingState) => void;
  isFooterVisible?: boolean;
  emptyText?: string | ReactElement;
  amountOfSkeletonRows?: number;
  onRowClick?: (index: number) => void;
  variant?: TableProps['variant'] & 'borderless';
  spannedCellRenderer?: (value: unknown) => ReactElement;
  colGroup?: ReactElement;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getRowSx?: (row: any) => BoxProps['sx'];
}

const ReactTableV8 = <TData extends TableData>({
  data,
  columns,
  minWidth = 'unset',
  pagination = false,
  initialState,
  isLoading = false,
  onSort,
  isFooterVisible,
  emptyText,
  isHeaderSticky,
  amountOfSkeletonRows,
  onRowClick,
  variant,
  colGroup,
  spannedCellRenderer = (value) => <>{value}</>,
  getRowSx,
  ...otherProps
}: ReactTableV8Props<TData> & BoxProps) => {
  const [isScrollable, setIsScrollable] = useBoolean();

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(pagination && { getPaginationRowModel: getPaginationRowModel() }),
    initialState: {
      sorting: initialState?.sorting,
      pagination: initialState?.pagination || { pageIndex: 0, pageSize: 10 },
    },
  });

  useEffect(() => {
    onSort && onSort(table.getState().sorting);
  }, [table.getState().sorting, onSort]);

  const rows = table.getRowModel().rows;
  const pageCount = table.getPageCount();
  const pageIndex = table.getState().pagination.pageIndex;

  return (
    <VStack alignItems="stretch" spacing="6">
      <ReactTableContainer
        isScrollable={isScrollable}
        setIsScrollable={setIsScrollable}
        isEmpty={data?.length === 0 && !emptyText}
        {...otherProps}
        {...(variant === 'borderless' && {
          borderRadius: 'none',
          borderLeft: 'none',
          borderRight: 'none',
        })}
      >
        <Table
          variant={variant}
          minW={minWidth}
          boxShadow="unset"
          border="unset"
        >
          {colGroup}
          <ReactTableV8Head
            headerGroups={table.getHeaderGroups()}
            isHeaderSticky={isHeaderSticky}
            isScrollable={isScrollable && !isLoading}
          />
          {isLoading ? (
            <TableBodySkeletonLoading
              amountOfColumns={table.getHeaderGroups()[0]?.headers.length || 0}
              amountOfRows={amountOfSkeletonRows}
            />
          ) : (
            <ReactTableV8Body
              rows={rows}
              isScrollable={isScrollable && !isLoading}
              onRowClick={onRowClick}
              spannedCellRenderer={spannedCellRenderer}
              getRowSx={getRowSx}
            />
          )}
          {isFooterVisible && (
            <ReactTableV8Footer footerGroups={table.getFooterGroups()} />
          )}
        </Table>
        {!isLoading && emptyText && data?.length === 0 && (
          <TableFiller>
            <Text>{emptyText}</Text>
          </TableFiller>
        )}
      </ReactTableContainer>
      {pagination && pageCount > 1 && (
        <Flex justifyContent="flex-end">
          <Pagination
            onPrev={() => table.previousPage()}
            onNext={() => table.nextPage()}
            onPageClick={(currentPage) => table.setPageIndex(currentPage - 1)}
            currentPage={pageIndex + 1}
            pages={pageCount}
          />
        </Flex>
      )}
    </VStack>
  );
};

export default ReactTableV8;
